import React, {
  useCallback, useContext, useEffect, useRef, FC, memo, useMemo,
} from 'react';
import { Textarea, TextareaValueData } from '@fluentai/textarea';
import { mergeClasses } from '@fluentui/react-components';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';

export interface IChatInputProps {
  sendDisabled?: boolean;
  inputDisabled?: boolean;
  className?: string;
  maxLength?: number;
  value: string;
  onChange(_ev: any, { value }: TextareaValueData): void;
  postMessage(_evt: any, { value }: TextareaValueData): void;
}

export const ChatInput: FC<IChatInputProps> = memo((props) => {
  const {
    className,
    maxLength,
    value,
    inputDisabled,
    sendDisabled,
    onChange,
    postMessage,
  } = props;
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const i18n = useContext(I18nContext) as I18nModel;

  useEffect(() => {
    textAreaRef.current?.focus();
  }, [sendDisabled]);

  const hotKeyHandler = useCallback((evt: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (evt.key === 'Enter' && !evt.shiftKey && value.trim().length > 0 && !sendDisabled) {
      postMessage(null, { value });
    }
  }, [postMessage, value]);

  const sendButtonProps = useMemo(() => ({
    disabled: sendDisabled || value.trim().length === 0,
  }), [sendDisabled, value]);

  const onSubmit = useCallback((_evt: any, { value: message }: TextareaValueData): void => {
    postMessage(_evt, { value: message });
  }, [postMessage]);

  return (
    <Textarea
      ref={textAreaRef}
      onKeyUp={hotKeyHandler}
      className={mergeClasses(className, 'chat-input')}
      disabled={inputDisabled}
      mode="sidecar"
      aria-label={i18n.getString(_TL_('Ask a question'))}
      placeholder={i18n.getString(_TL_('Ask a question'))}
      showCount
      onSubmit={onSubmit}
      onChange={onChange}
      characterCount={value.length}
      value={value}
      maxLength={maxLength}
      send={sendButtonProps}
    />
  );
});

ChatInput.defaultProps = {
  inputDisabled: undefined,
  sendDisabled: undefined,
  className: '',
  maxLength: 500,
};
